import type { DeviceState, PointState } from "../generated/widget-fixture";
import { Frame, key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";
import { VIRTUAL_ABOVE, VirtualList } from "./VirtualList";

type Row = { id: string; d: DeviceState; p: PointState | undefined };

const ROW = 32;

const ORDER = { offline: 0, sleeping: 1, online: 2 } as const;

/** status_list: the devices of a group or space with their state and one value each. */
export function StatusList({ widget, input }: WidgetProps) {
  const fmt = useWidgetText();
  const cfg = widget.config ?? {};
  const dp = typeof cfg.datapoint === "string" ? cfg.datapoint : undefined;
  const alarmed = new Set((input.alarms ?? []).filter((a) => !a.acknowledged).map((a) => a.device));
  let rows: Row[] = (input.members ?? []).flatMap((id) => {
    const d = input.devices?.[id];
    return d ? [{ id, d, p: dp ? input.points?.[key(id, dp)] : undefined }] : [];
  });
  if (cfg.only_problems === true) rows = rows.filter((r) => r.d.state === "offline" || alarmed.has(r.id));
  const num = (r: (typeof rows)[number]) => (typeof r.p?.latest?.value === "number" ? r.p.latest.value : -Infinity);
  if (cfg.sort === "status") rows.sort((a, b) => ORDER[a.d.state] - ORDER[b.d.state]);
  // Rows without a value go last; subtracting two -Infinity would give NaN and an order that changes on every draw.
  else if (cfg.sort === "value") rows.sort((a, b) => (num(a) === num(b) ? 0 : num(b) > num(a) ? 1 : -1));
  else rows.sort((a, b) => a.d.name.localeCompare(b.d.name, fmt.locale, { numeric: true }));
  const members = input.members ?? [];
  const offline = members.some((id) => input.devices?.[id]?.state === "offline");
  return (
    <Frame widget={widget} state={members.length === 0 ? "empty" : offline ? "offline" : "normal"} title={widget.title}>
      {members.length === 0 ? <p className="text-gray-700">{fmt.t("list.noDevices")}</p> : <StatusRows rows={rows} />}
    </Frame>
  );
}

function StatusRow({ r }: { r: Row }) {
  const fmt = useWidgetText();
  const v = r.d.state !== "offline" && r.p && typeof r.p.latest?.value === "number" ? r.p.latest.value : undefined;
  return (
    <>
      <span
        aria-hidden="true"
        className={`size-2 rounded-full ${r.d.state === "offline" ? "bg-red-600" : r.d.state === "sleeping" ? "bg-gray-400" : "bg-green-600"}`}
      />
      <span className="flex-1 truncate text-gray-900">{r.d.name}</span>
      <span className="text-xs text-gray-700">{fmt.deviceState(r.d.state)}</span>
      {v !== undefined && r.p && <span className="w-16 text-right tabular-nums">{fmt.value(v, r.p)}</span>}
    </>
  );
}

function StatusRows({ rows }: { rows: Row[] }) {
  if (rows.length <= VIRTUAL_ABOVE) {
    return (
      <ul className="min-h-0 flex-1 divide-y divide-gray-100 overflow-auto">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center gap-2 py-1.5">
            <StatusRow r={r} />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <VirtualList
      items={rows}
      rowKey={(r) => r.id}
      row={(r) => <StatusRow r={r} />}
      rowHeight={ROW}
      className="border-b border-gray-100"
    />
  );
}
